import React, { useEffect, useState } from 'react';
import { CheckCircle, ArrowLeft, Coins } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { supabase } from '../supabase/client';
import { getUserOrders } from '../services/stripeService';
import { getProductByPriceId } from '../stripe-config';
import { addTokens, TokenTransactionType } from '../services/tokenService';
import { analyticsService } from '../services/analyticsService';
import TruckLogo from '../components/TruckLogo';
import BusinessFooter from '../components/BusinessFooter';

const SuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [orderDetails, setOrderDetails] = useState<any>(null);
  const [tokensAdded, setTokensAdded] = useState(false);

  useEffect(() => {
    const processSuccessfulPayment = async () => {
      if (!sessionId) {
        setError('No se encontró el ID de sesión');
        setLoading(false);
        return;
      }

      try {
        // Get current user
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        
        if (userError || !user) {
          console.error("SuccessPage: Error de autenticación de usuario:", userError);
          setError('Usuario no autenticado');
          setLoading(false);
          return;
        }
        console.log("SuccessPage: ID de usuario autenticado:", user.id);

        // Wait a moment for webhook to process
        console.log("SuccessPage: Esperando 5 segundos para que el webhook procese...");
        await new Promise(resolve => setTimeout(resolve, 5000));
        console.log("SuccessPage: Espera finalizada.");

        // Fetch the order details
        console.log("SuccessPage: Obteniendo órdenes del usuario...");
        const orders = await getUserOrders();
        const order = orders.find(o => o.checkout_session_id === sessionId);

        if (!order) {
          console.error("SuccessPage: Información de la orden no encontrada para el ID de sesión:", sessionId);
          setError('No se encontró información de la orden');
          setLoading(false);
          return;
        }
        console.log("SuccessPage: Orden encontrada:", order);

        setOrderDetails(order);

        // Find the product details
        console.log("SuccessPage: Buscando producto por ID de precio:", order.price_id);
        const product = getProductByPriceId(order.price_id);
        
        if (!product) {
          console.error("SuccessPage: Producto no encontrado en STRIPE_PRODUCTS para el ID de precio:", order.price_id);
          setError("Error: Producto de Stripe no encontrado.");
          setLoading(false);
          return;
        }
        console.log("SuccessPage: Detalles del producto encontrados:", product);
        
        if (!tokensAdded) {
          // Add tokens to user account
          console.log(`SuccessPage: Intentando añadir ${product.tokens} tokens al usuario ${user.id}...`);
          const success = await addTokens(
            user.id,
            product.tokens,
            TokenTransactionType.PURCHASED,
            `Compra de ${product.tokens} tokens - ${product.name}`,
            user.id,
            sessionId // Pass the checkout session ID to prevent duplicates
          );

          if (success) {
            setTokensAdded(true);
            console.log("SuccessPage: Tokens añadidos exitosamente. Rastreando compra...");
            
            // Track successful purchase
            analyticsService.trackTokenPurchase({
              packageId: product.id,
              tokens: product.tokens,
              amount: product.price,
              currency: 'MXN'
            });
          } else {
            console.error("SuccessPage: Falló la adición de tokens. 'addTokens' devolvió false.");
            setError("Error al añadir tokens a la cuenta del usuario.");
          }
        } else {
          console.log("SuccessPage: Tokens ya añadidos, omitiendo llamada a addTokens.");
        }

        setLoading(false);
      } catch (err: any) {
        console.error('SuccessPage: Error no manejado al procesar el pago exitoso:', err);
        setError('Error al procesar el pago exitoso');
        setLoading(false);
      }
    };

    processSuccessfulPayment();
  }, [sessionId, tokensAdded]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Procesando tu compra...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="bg-red-100 p-3 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <span className="text-red-600 text-2xl">⚠️</span>
            </div>
            <h1 className="text-xl font-bold text-gray-800 mb-2">Error</h1>
            <p className="text-gray-600 mb-6">{error}</p>
            <Link
              to="/"
              className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
            >
              <ArrowLeft className="mr-2" size={16} />
              Volver al inicio
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const product = orderDetails ? getProductByPriceId(orderDetails.price_id) : null;

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-primary text-white shadow-md">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center">
            <TruckLogo size={28} className="text-white mr-2" />
            <h1 className="text-xl font-bold uppercase">COTIZATRIN</h1>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-2xl mx-auto">
          <div className="text-center">
            <div className="bg-green-100 p-4 rounded-full w-20 h-20 mx-auto mb-6 flex items-center justify-center">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
            
            <h1 className="text-3xl font-bold text-gray-800 mb-2">¡Compra Exitosa!</h1>
            <p className="text-gray-600 mb-8">Tu pago ha sido procesado correctamente</p>

            {product && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
                <h2 className="text-lg font-semibold text-blue-800 mb-4">Detalles de tu compra</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <div className="text-blue-700">Producto:</div>
                  <div className="text-blue-900 font-medium">{product.name}</div>
                  
                  <div className="text-blue-700">Créditos:</div>
                  <div className="text-blue-900 font-medium flex items-center">
                    <Coins className="h-4 w-4 mr-1" />
                    {product.tokens} créditos
                  </div>
                  
                  <div className="text-blue-700">Total pagado:</div>
                  <div className="text-blue-900 font-medium">
                    {product.price > 0 ? (
                      new Intl.NumberFormat('es-MX', {
                        style: 'currency',
                        currency: 'MXN'
                      }).format(product.price / 100) // Convert from cents to pesos
                    ) : (
                      'GRATIS'
                    )}
                  </div>
                </div>
              </div>
            )}

            {tokensAdded && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <p className="text-green-800 font-medium">
                  ✅ Los créditos han sido añadidos a tu cuenta correctamente
                </p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/"
                className="inline-flex items-center px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
              >
                <ArrowLeft className="mr-2" size={16} />
                Volver al Dashboard
              </Link>
            </div>
          </div>
        </div>
      </main>

      <BusinessFooter variant="minimal" />
    </div>
  );
};

export default SuccessPage;